import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import useIsMobile from './hooks/useIsMobile';
import useIsAdmin from './hooks/useIsAdmin';
import MobileMenu from './MobileMenu';
import HeaderSearch from './search/HeaderSearch';
import { usePageLivery } from './pageLivery';
import './header.css';

// Header assets are expected under public/assets
// Brand wordmark for the nav on dark backgrounds (see DESIGN.md). Replaces the
// legacy /assets/nexxup-logo-header.webp; the mark-only asset is
// nexxup-mark-color.png.
const imgNexxupWordmark =
  `${process.env.PUBLIC_URL || ''}/brand/nexxup-wordmark-color-light.png`;
const headerProfileIcon =
  `${process.env.PUBLIC_URL || ''}/assets/header-profile-icon.svg`;
const headerBagIcon =
  `${process.env.PUBLIC_URL || ''}/assets/header-bag-icon.svg`;
const brandMark =
  `${process.env.PUBLIC_URL || ''}/brand/nexxup-mark-color.png`;

// At and below this width the compact hamburger header replaces the full
// nav, which needs up to ~1230px even at its tighter laptop size (header.css).
// index.css switches --site-header-h (56px / 72px) at the same width.
const HEADER_COMPACT_MAX = 1239;

// The desktop nav's page links; MobileMenu.jsx lists the same pages.
const NAV_LINKS = [
  { to: '/leaderboard', label: 'Leaderboard' },
  { to: '/heatmap', label: 'Heat Map' },
  { to: '/discover', label: 'Discover' },
  { to: '/scout-score', label: 'Scout Score' },
  { to: '/hall-of-fame', label: 'Hall of Fame' },
  { to: '/artist-application?type=fan-submission', label: 'Submit an Artist' },
];

// While an artist profile with a livery is open (pageLivery.js), the header
// restyles to match it. `vars` re-point the colour variables every link and
// icon below already reads, so the nav itself needs no per-livery code.
const HEADER_LIVERIES = {
  desktop95: {
    style: {
      background: '#c0c0c0',
      borderBottom: '0',
      boxShadow: 'inset 0 -1px #808080, inset 0 -2px #0a0a0a',
      fontFamily: "'Pixelify Sans', monospace",
      '--text-secondary': '#000',
      '--text-primary': '#000080',
      '--nx-text': '#000',
      '--nx-input-border': '#808080',
      '--hdr-icon-filter': 'invert(1)',
    },
    wordmark: 'nexxup',
    markScale: 1.15,
    textScale: 1,
    wordmarkStyle: { fontFamily: "'Pixelify Sans', monospace", fontWeight: 700, color: '#000', letterSpacing: 0 },
  },
  mynexxup: {
    style: {
      background: '#003399',
      borderBottom: '1px solid #002266',
      '--text-secondary': 'rgba(255, 255, 255, 0.85)',
      '--text-primary': '#fff',
      '--nx-text': '#fff',
      '--nx-input-border': 'rgba(255, 255, 255, 0.4)',
    },
    wordmark: 'mynexxup.com',
    // The longest wordmark - kept compact so the desktop nav still fits.
    markScale: 1.05,
    textScale: 0.85,
    wordmarkStyle: { fontFamily: 'Arial, Helvetica, sans-serif', fontWeight: 700, color: '#fff', letterSpacing: '-1px' },
  },
};

// The nav logo: the brand wordmark normally, or the livery's own wordmark
// (the XX mark plus text) while a liveried profile is open.
function HeaderLogo({ livery, height }) {
  if (!livery) {
    return <img src={imgNexxupWordmark} alt="Nexxup" style={{ height, width: 'auto', display: 'block' }} />;
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: Math.round(height / 4) }}>
      <img src={brandMark} alt="" style={{ height: Math.round(height * livery.markScale), width: 'auto', display: 'block' }} />
      <span style={{ ...livery.wordmarkStyle, fontSize: Math.round(height * livery.textScale), lineHeight: 1, whiteSpace: 'nowrap' }}>
        {livery.wordmark}
      </span>
    </span>
  );
}

// The signed-in user's photo (users/{uid}.photoURL, else the one from their
// Google sign-in) in the account spot, or the generic icon without one or if
// it won't load. `size` is the photo's diameter.
function AccountImage({ photoURL, size, iconSize }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [photoURL]);
  if (photoURL && !failed) {
    return (
      <img
        src={photoURL}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        style={{ height: size, width: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return (
    <img
      src={headerProfileIcon}
      alt=""
      style={{ height: iconSize, width: iconSize, display: 'block', filter: 'var(--hdr-icon-filter, none)' }}
    />
  );
}

function Header({ user, userProfile, getAccountPath }) {
  const accountPhoto = (user && (userProfile?.photoURL || user.photoURL)) || null;
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const isMobile = useIsMobile(HEADER_COMPACT_MAX);
  const liveryHeader = HEADER_LIVERIES[usePageLivery()] || null;

  // Real admin check (mirrors firestore.rules' isAdmin field via
  // useIsAdmin), not a hardcoded email - AdminReviewPanel used to be the
  // one place on the profile page that gated on the real field, so any
  // admin account other than the original hardcoded email still had a way
  // in. Now that the nav button is the only admin entry point, it has to
  // reflect the real field itself.
  const { isAdmin } = useIsAdmin(user);

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  if (isMobile) {
    return (
      <>
        {/* Flat header with a full-width hairline (homepage mockup). Height is
            pinned to --site-header-h (56px here) via box-sizing, so the pages
            that size themselves against it (heat map, sign-up) stay exact. */}
        <header style={{
          background: 'var(--nx-bg)',
          borderBottom: '1px solid var(--nx-border)',
          boxSizing: 'border-box',
          
          position: 'sticky',
          top: 0,
          paddingTop: 'var(--sat)',
          height: 'calc(var(--site-header-h) + var(--sat))',
          zIndex: 1000,
          ...liveryHeader?.style
        }}>
          <nav style={{
            padding: '0 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%'
          }}>

            {/* Hamburger Menu Button */}
            <button
              onClick={toggleMobileMenu}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-primary)',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minWidth: '40px'
              }}
            >
              ☰
            </button>

            {/* Centered Logo */}
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                textDecoration: 'none'
              }}
            >
              <HeaderLogo livery={liveryHeader} height={21} />
            </Link>

            {/* Profile + Saved items. Padding paired with an equal negative
                margin (same trick used for the SoundCloud link/back-link in
                artists_table.jsx) grows the tappable area to ~40x40 without
                changing the icons' visual size or the row's layout - the
                18x18 icon with zero padding was a hard-to-tap target on a
                phone. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <HeaderSearch compact />
              <Link
                to={user ? getAccountPath() : '/signup'}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 11, margin: -11 }}
                aria-label={user ? 'My account' : 'Sign up'}
              >
                <AccountImage photoURL={accountPhoto} size={28} iconSize={18} />
              </Link>
              <Link
                to="/shop"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 11, margin: -11 }}
                aria-label="Shop"
              >
                <img src={headerBagIcon} alt="" style={{ height: 18, width: 18, display: 'block', filter: 'var(--hdr-icon-filter, none)' }} />
              </Link>
            </div>
          </nav>
        </header>

        <MobileMenu
          isOpen={showMobileMenu}
          onClose={() => setShowMobileMenu(false)}
          isAdmin={isAdmin}
          user={user}
          getAccountPath={getAccountPath}
        />
      </>
    );
  }

  // Desktop version.
  // Flat header with a full-width hairline (homepage mockup). Height is pinned
  // to --site-header-h (72px here) via box-sizing, as above.
  return (
    <header
      style={{
        background: 'var(--nx-bg)',
        borderBottom: '1px solid var(--nx-border)',
        boxSizing: 'border-box',
        height: 'calc(var(--site-header-h) + var(--sat))',
        position: 'sticky',
        top: 0,
        paddingTop: 'var(--sat)',
        zIndex: 1000,
        ...liveryHeader?.style
      }}
    >
      <nav className="nx-hdr-nav">
        {/* Logo/Brand */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Link 
            to="/" 
            style={{
              textDecoration: 'none', 
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <HeaderLogo livery={liveryHeader} height={26} />
          </Link>
        </div>

        {/* Navigation Links */}
        <div className="nx-hdr-links">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nx-hdr-link${isActive ? ' is-active' : ''}`}>{label}</NavLink>
          ))}

          {/* Info Dropdown */}
          <div 
            style={{ position: 'relative' }}
            onMouseEnter={() => setShowAboutDropdown(true)}
            onMouseLeave={() => setShowAboutDropdown(false)}
          >
            <button className={`nx-hdr-info${showAboutDropdown ? ' is-open' : ''}`}>
              Info
              <span style={{
                fontSize: '12px',
                transform: showAboutDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>▼</span>
            </button>

            {/* Dropdown Menu */}
            {showAboutDropdown && (
              <>
                {/* Invisible bridge to prevent dropdown from closing */}
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  width: '100%',
                  height: '2px',
                  background: 'transparent'
                }} />
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  marginTop: '2px', // Reduced gap to prevent mouse leaving
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  minWidth: '200px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  overflow: 'hidden',
                  animation: 'fadeIn 0.2s ease',
                  zIndex: 1000 // Ensure dropdown stays on top
                }}>
                <Link
                  to="/about"
                  style={{
                    display: 'block',
                    padding: '12px 16px',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderBottom: '1px solid var(--border-color)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--bg-elevated)';
                    e.target.style.color = 'var(--accent)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'var(--text-primary)';
                  }}
                >
                  About Nexxup
                </Link>
                <Link
                  to="/help"
                  style={{
                    display: 'block',
                    padding: '12px 16px',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderBottom: '1px solid var(--border-color)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--bg-elevated)';
                    e.target.style.color = 'var(--accent)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'var(--text-primary)';
                  }}
                >
                  Help
                </Link>
                <Link
                  to="/copyright"
                  style={{
                    display: 'block',
                    padding: '12px 16px',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderTop: '1px solid var(--border-color)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--bg-elevated)';
                    e.target.style.color = 'var(--accent-alt)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'var(--text-primary)';
                  }}
                >
                  Copyright &amp; Takedowns
                </Link>
                <Link
                  to="/privacy"
                  style={{
                    display: 'block',
                    padding: '12px 16px',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderTop: '1px solid var(--border-color)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--bg-elevated)';
                    e.target.style.color = 'var(--accent-alt)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'var(--text-primary)';
                  }}
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  style={{
                    display: 'block',
                    padding: '12px 16px',
                    textDecoration: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderTop: '1px solid var(--border-color)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--bg-elevated)';
                    e.target.style.color = 'var(--accent-alt)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = 'var(--text-primary)';
                  }}
                >
                  Terms of Service
                </Link>
                </div>
              </>
            )}
          </div>

          {isAdmin && (
            <Link to="/admin" className="nx-hdr-link nx-hdr-admin">
              🛠️ Admin
            </Link>
          )}
        </div>

        {/* Right side: shop, then the auth cluster. Logged out shows the
            "Log in" + the light "Sign up" pill (Sign up routes to the
            account-type chooser at /signup, which forwards to the fan or artist
            signup); logged in keeps the account icon. Real routes preserved. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HeaderSearch />
          <Link
            to="/shop"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 10,
              borderRadius: '50%',
              border: '1px solid var(--nx-input-border)',
              background: 'transparent'
            }}
            aria-label="Shop"
          >
            <img
              src={headerBagIcon}
              alt=""
              style={{ height: 20, width: 20, display: 'block', filter: 'var(--hdr-icon-filter, none)' }}
            />
          </Link>
          {user ? (
            <Link
              to={getAccountPath()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: accountPhoto ? 0 : 10,
                borderRadius: '50%',
                border: '1px solid var(--nx-input-border)',
                background: 'transparent'
              }}
              aria-label="My account"
            >
              <AccountImage photoURL={accountPhoto} size={40} iconSize={20} />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                style={{
                  textDecoration: 'none',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: 'var(--nx-text)',
                  padding: '10px 14px',
                  whiteSpace: 'nowrap'
                }}
              >
                Log in
              </Link>
              <Link
                to="/signup"
                style={{
                  textDecoration: 'none',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'var(--nx-primary-text)',
                  background: 'var(--nx-primary-bg)',
                  padding: '10px 18px',
                  borderRadius: 'var(--nx-radius-pill)',
                  whiteSpace: 'nowrap'
                }}
              >
                Sign up
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Add CSS animation keyframes */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </header>
  );
}

export default Header;
